import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import Backend from 'i18next-http-backend';
import numbro from 'numbro';
import 'numbro/dist/languages.min';
import { useEffect, useRef } from 'react';
import { initReactI18next } from 'react-i18next';
import { useStorage } from '../../hooks/use-storage';
import { isDevEnv } from '../manager/host-manager';

dayjs.extend(relativeTime);

i18n
  .use(Backend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    fallbackLng: 'en',
    debug: isDevEnv,
    backend: {
      loadPath: '/locales/{{lng}}/{{ns}}.json',
    },
    interpolation: {
      escapeValue: false,
    },
  });

function useI18nSetup() {
  const { language } = useStorage();
  const languageChangingCounterRef = useRef(0);

  useEffect(() => {
    dayjsSetup();
    numeralSetup();
    i18nextSetup();

    if (language != null) {
      refreshScreen();
    }
  }, [language]);

  function refreshScreen() {
    if (languageChangingCounterRef.current > 0) {
      // eslint-disable-next-line no-undef
      window.location.reload();
    }

    languageChangingCounterRef.current += 1;
  }

  async function dayjsSetup() {
    if (language == null) return;

    const promises = [
      import(`dayjs/locale/en`),
      import(`dayjs/locale/pt`),
      import(`dayjs/locale/pt-br`),
      import(`dayjs/locale/es`),
      import(`dayjs/locale/fr`),
    ];

    await Promise.all(promises);
    dayjs.locale(language.tag);
  }

  async function numeralSetup() {
    if (language == null) return;

    let promises = [
      import(`numbro/languages/en-GB.js`),
      import(`numbro/languages/pt-PT.js`),
      import(`numbro/languages/pt-BR.js`),
      import(`numbro/languages/es-ES.js`),
      import(`numbro/languages/fr-FR.js`),
    ];

    const locales = await Promise.all(promises);
    promises = locales.map((l) => numbro.registerLanguage(l.default));
    await Promise.all(promises);

    numbro.setLanguage(language.tag);
    numbro.setDefaults({
      thousandSeparated: true,
      mantissa: 2,
    });
  }

  async function i18nextSetup() {
    if (i18n && language) {
      // const logMessage = {
      //   title: 'i18nSetup',
      //   currentLang: i18n.language,
      //   newLang: language.tag,
      // };
      await i18n.changeLanguage(language.tag);
      // logMessage.updatedLang = i18n.language;
      // captureMessage(Flatted.stringify(logMessage)); // must be imported from sentry-setup
    }
  }
}

export default i18n;

export { i18n, useI18nSetup };
